// Dashboard API Service - Backend Integration
import { apiGet, apiPost } from '../services/apiClient';

// Default tenant ID for public dashboard (no authentication required)
const DEFAULT_TENANT_ID = 1;

/**
 * Get dashboard metrics
 * Gateway path: /gateway/registration/api/signup/dashboard/metrics
 * @param {number} days - Number of days for trends (default: 30)
 * @param {number} tenantId - Tenant ID (optional, defaults to 1 for public dashboard)
 * @returns {Promise<Object>} Dashboard metrics response
 */
export const getDashboardMetrics = async (days = 30, tenantId = DEFAULT_TENANT_ID) => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/dashboard/metrics', { 
      days,
      tenantId 
    });
    
    // Backend returns: { data: { status, message, data: DashboardMetricsResponse } }
    if (response?.data?.data?.data) {
      return response.data.data.data;
    } else if (response?.data?.data) {
      return response.data.data;
    } else if (response?.data) {
      return response.data;
    }
    return response;
  } catch (error) {
    console.error('Error fetching dashboard metrics:', error);
    throw error;
  }
};

/**
 * Get ticket trends
 * Gateway path: /gateway/registration/api/signup/dashboard/trends
 * @param {number} days - Number of days for trends (default: 30)
 * @param {number} tenantId - Tenant ID (optional, defaults to 1 for public dashboard)
 * @returns {Promise<Object>} Ticket trends response
 */
export const getTicketTrends = async (days = 30, tenantId = DEFAULT_TENANT_ID) => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/dashboard/trends', { 
      days,
      tenantId 
    });
    
    // Backend returns: { data: { status, message, data: TicketTrendsResponse } }
    if (response?.data?.data?.data) {
      return response.data.data.data;
    } else if (response?.data?.data) {
      return response.data.data;
    } else if (response?.data) {
      return response.data;
    }
    return response;
  } catch (error) {
    console.error('Error fetching ticket trends:', error);
    throw error;
  }
};

/**
 * Get performance metrics
 * Gateway path: /gateway/registration/api/signup/dashboard/performance
 * @param {number} days - Number of days for metrics (default: 30)
 * @param {number} tenantId - Tenant ID (optional, defaults to 1 for public dashboard)
 * @returns {Promise<Object>} Performance metrics response
 */
export const getPerformanceMetrics = async (days = 30, tenantId = DEFAULT_TENANT_ID) => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/dashboard/performance', { 
      days,
      tenantId 
    });
    
    // Backend returns: { data: { status, message, data: PerformanceMetricsResponse } }
    if (response?.data?.data?.data) {
      return response.data.data.data;
    } else if (response?.data?.data) {
      return response.data.data;
    } else if (response?.data) {
      return response.data;
    }
    return response;
  } catch (error) {
    console.error('Error fetching performance metrics:', error);
    throw error;
  }
};

/**
 * Get drill-down data
 * Gateway path: /gateway/registration/api/signup/dashboard/drill-down
 * @param {Object} drillDownRequest - Drill-down request parameters
 * @param {string} drillDownRequest.metricType - Type of metric ('status', 'priority', 'category', 'department', 'agent', 'date')
 * @param {string} drillDownRequest.metricValue - Specific value to drill down into
 * @param {string} drillDownRequest.startDate - Start date (ISO format)
 * @param {string} drillDownRequest.endDate - End date (ISO format)
 * @param {number} drillDownRequest.tenantId - Tenant ID (optional, defaults to 1 for public dashboard)
 * @param {number} drillDownRequest.organizationId - Organization ID (optional)
 * @param {number} drillDownRequest.departmentId - Department ID (optional)
 * @param {number} drillDownRequest.agentId - Agent ID (optional)
 * @param {string} drillDownRequest.statusCode - Status code (optional)
 * @param {string} drillDownRequest.priorityCode - Priority code (optional)
 * @param {number} drillDownRequest.categoryId - Category ID (optional)
 * @param {number} drillDownRequest.limit - Limit for pagination (optional)
 * @param {number} drillDownRequest.offset - Offset for pagination (optional)
 * @returns {Promise<Object>} Drill-down data response
 */
export const getDrillDownData = async (drillDownRequest) => {
  try {
    // Ensure tenantId is set to default if not provided
    const requestWithTenant = {
      ...drillDownRequest,
      tenantId: drillDownRequest.tenantId || DEFAULT_TENANT_ID
    };
    
    const response = await apiPost('/gateway/registration/api/signup/dashboard/drill-down', requestWithTenant);
    
    // Backend returns: { data: { status, message, data: { ... } } }
    if (response?.data?.data?.data) {
      return response.data.data.data;
    } else if (response?.data?.data) {
      return response.data.data;
    } else if (response?.data) {
      return response.data;
    }
    return response;
  } catch (error) {
    console.error('Error fetching drill-down data:', error);
    throw error;
  }
};

